import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Grid,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Search,
    FilterList,
    Add,
    Refresh,
    CalendarToday,
    Schedule,
    History
} from "@mui/icons-material";
import ScheduledCalendarList from "../../Components/ScheduledCalendarList";
import { useNavigate } from "react-router-dom";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`scheduled-classes-tabpanel-${index}`}
            aria-labelledby={`scheduled-classes-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const ScheduledCalendarListPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    
    const [tabValue, setTabValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSubject, setSelectedSubject] = useState<number | "">("");
    const [selectedTutor, setSelectedTutor] = useState<number | "">("");
    const [showFilters, setShowFilters] = useState(false);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleCreateClass = () => {
        navigate('/online-tutoring/schedule');
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <Box sx={{ 
            minHeight: 'calc(100vh - 64px)',
            bgcolor: '#f9fafb',
            p: isMobile ? 1 : 2
        }}>
            {/* Header */}
            <Paper sx={{ 
                p: isMobile ? 2 : 3, 
                mb: 2, 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 600, 
                        color: '#3c4043',
                        fontSize: isMobile ? '1.5rem' : '2rem'
                    }}>
                        Scheduled Classes
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Filters">
                            <IconButton onClick={() => setShowFilters(!showFilters)}>
                                <FilterList />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateClass}
                            sx={{ 
                                bgcolor: '#1a73e8',
                                '&:hover': { bgcolor: '#1765cc' }
                            }}
                        >
                            {isMobile ? '' : 'Schedule Class'}
                        </Button>
                    </Box>
                </Box>

                {/* Search Bar */}
                <TextField
                    fullWidth
                    placeholder="Search classes by title, tutor, or subject..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <Search sx={{ mr: 1, color: '#5f6368' }} />
                    }}
                    sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        }
                    }}
                />

                {/* Filters */}
                {showFilters && (
                    <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f8f9fa', 
                        borderRadius: 2,
                        border: '1px solid #ebedef'
                    }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Subject</InputLabel>
                                    <Select
                                        value={selectedSubject}
                                        label="Subject"
                                        onChange={(e) => setSelectedSubject(e.target.value as number | "")}
                                    >
                                        <MenuItem value="">All Subjects</MenuItem>
                                        <MenuItem value={1}>Mathematics</MenuItem>
                                        <MenuItem value={2}>Physics</MenuItem>
                                        <MenuItem value={3}>Chemistry</MenuItem>
                                        <MenuItem value={4}>Biology</MenuItem>
                                        {/* Add more subjects as needed */}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Tutor</InputLabel>
                                    <Select
                                        value={selectedTutor}
                                        label="Tutor"
                                        onChange={(e) => setSelectedTutor(e.target.value as number | "")}
                                    >
                                        <MenuItem value="">All Tutors</MenuItem>
                                        <MenuItem value={295}>mani</MenuItem>
                                        <MenuItem value={428}>regan</MenuItem>
                                        {/* Add more tutors as needed */}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={12} md={6}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedSubject("");
                                            setSelectedTutor("");
                                            setSearchQuery("");
                                        }}
                                    >
                                        Clear Filters
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Paper>

            {/* Tabs */}
            <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange}
                        variant={isMobile ? "fullWidth" : "standard"}
                        sx={{ 
                            '& .MuiTab-root': {
                                minHeight: 48,
                                textTransform: 'none',
                                fontWeight: 500
                            }
                        }}
                    >
                        <Tab 
                            icon={<Schedule />} 
                            label="Upcoming" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<CalendarToday />} 
                            label="Today" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<History />} 
                            label="All Classes" 
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <ScheduledCalendarList 
                        showUpcomingOnly={true}
                        subjectId={selectedSubject || undefined}
                        tutorId={selectedTutor || undefined}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ScheduledCalendarList 
                        showUpcomingOnly={false}
                        showPastClasses={false}
                        subjectId={selectedSubject || undefined}
                        tutorId={selectedTutor || undefined}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <ScheduledCalendarList 
                        showPastClasses={true}
                        subjectId={selectedSubject || undefined}
                        tutorId={selectedTutor || undefined}
                    />
                </TabPanel>
            </Paper>

            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Schedule sx={{ fontSize: 32, color: '#1a73e8', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Total Classes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            All scheduled sessions
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <CalendarToday sx={{ fontSize: 32, color: '#34a853', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Today
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Classes scheduled for today
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <History sx={{ fontSize: 32, color: '#fbbc04', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Completed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Past sessions
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <FilterList sx={{ fontSize: 32, color: '#ea4335', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Filtered
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Active filters applied
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ScheduledCalendarListPage;
